import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useUserProfile, useUpdateUserProfile } from "../../hooks/useProfile";
import { OrganizationDto } from "../../dtos/userProfile";
import { useOrganizationsService } from "@/modules/organizations/hooks/use-organizations";
import { ChangePasswordConfirmDialog } from "./components/ChangePasswordConfirmDialog"
import { Spinner } from "@/modules/shared/ui/components/spinner"
import { Button } from "@/modules/shared/ui/components/button"
import { Input } from "@/modules/shared/ui/components/input"
import { Badge } from "@/modules/shared/ui/components/badge"
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/modules/shared/ui/components/card"
import { Separator } from "@/modules/shared/ui/components/separator"
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/modules/shared/ui/components/form"
import {
  Calendar,
  Clock,
  Shield,
  Building2,
  ArrowRight,
  Save,
  X,
  Pencil,
  User,
} from "lucide-react"
import { toast } from "sonner"

const profileSchema = z.object({
  firstName: z.string().min(1, "First name is required"),
  lastName: z.string().min(1, "Last name is required"),
  phoneNumber: z
    .string()
    .min(9, "Please verify phone number")
    .max(10, "Phone number is too long"),
})

type ProfileFormValues = z.infer<typeof profileSchema>

export default function UserProfilePage() {
  const navigate = useNavigate()
  const { data: profile, isLoading, error, refetch } = useUserProfile()
  const updateProfile = useUpdateUserProfile()
  const { currentOrganization } = useOrganizationsService()

  const [isEditing, setIsEditing] = useState(false)

  const stripCountryCode = (phone: string) =>
    phone.replace(/^\+?27/, "").replace(/^0/, "")

  const form = useForm<ProfileFormValues>({
    resolver: zodResolver(profileSchema),
    defaultValues: {
      firstName: "",
      lastName: "",
      phoneNumber: "",
    },
  })

  useEffect(() => {
    if (profile) {
      form.reset({
        firstName: profile.firstName,
        lastName: profile.lastName,
        phoneNumber: stripCountryCode(profile.phoneNumber),
      })
    }
  }, [profile, form])

  const handleEdit = () => {
    setIsEditing(true)
  }

  const handleCancel = () => {
    form.reset({
      firstName: profile?.firstName ?? "",
      lastName: profile?.lastName ?? "",
      phoneNumber: stripCountryCode(profile?.phoneNumber ?? ""),
    })
    setIsEditing(false)
  }

  const onSubmit = async (values: ProfileFormValues) => {
    try {
      await updateProfile.mutateAsync({
        firstName: values.firstName.trim(),
        lastName: values.lastName.trim(),
        phoneNumber: values.phoneNumber.trim(),
      })
      toast.success("Profile updated successfully!")
      form.reset(values)
      setIsEditing(false)
    } catch (err: unknown) {
      const msg =
        err instanceof Error ? err.message : "Failed to update profile."
      toast.error(msg)
    }
  }

  const handleViewOrgProfile = (orgId: string) => {
    navigate(`/account/profile/organization/${orgId}`)
  }

  const formatDate = (dateString: string | null | undefined) => {
    if (!dateString) return "N/A"
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
    })
  }

  const formatDateTime = (dateString: string | null | undefined) => {
    if (!dateString) return "Never"
    const date = new Date(dateString)
    // Guard against DateTime.MinValue ("0001-01-01T00:00:00") leaking from the backend
    if (isNaN(date.getTime()) || date.getUTCFullYear() < 2000) return "Never"
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    })
  }

  const [changePasswordOpen, setChangePasswordOpen] = useState(false)

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-96">
        <Spinner />
      </div>
    )
  }

  if (error || !profile) {
    return (
      <div className="flex flex-col items-center justify-center h-96 gap-4">
        <p className="text-lg text-muted-foreground">Failed to load profile</p>
        <Button onClick={() => refetch()}>Retry</Button>
      </div>
    )
  }

  const initials =
    (profile.firstName?.[0] || "") + (profile.lastName?.[0] || "")

  return (
    <div className="container mx-auto py-6 px-4 max-w-7xl">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-4">
            <div className="w-16 h-16 bg-primary rounded-full flex items-center justify-center text-primary-foreground text-2xl font-semibold">
              {initials || <User className="w-8 h-8" />}
            </div>
            <div>
              <h1 className="text-3xl font-bold text-foreground">
                {profile.firstName} {profile.lastName}
              </h1>
              <p className="text-muted-foreground mt-1">{profile.email}</p>
            </div>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left Column - Main Info */}
        <div className="lg:col-span-2 space-y-6">
          {/* Personal Information Card */}
          <Card className="bg-card border border-border shadow-sm">
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="text-xl">Personal Information</CardTitle>
                {!isEditing ? (
                  <Button onClick={handleEdit} variant="outline" size="sm">
                    <Pencil className="w-4 h-4 mr-2" />
                    Edit
                  </Button>
                ) : (
                  <div className="flex gap-2">
                    <Button
                      type="button"
                      onClick={handleCancel}
                      variant="outline"
                      size="sm"
                      disabled={updateProfile.isPending}
                    >
                      <X className="w-4 h-4 mr-2" />
                      Cancel
                    </Button>
                    <Button
                      type="submit"
                      form="profile-form"
                      size="sm"
                      disabled={updateProfile.isPending}
                    >
                      <Save className="w-4 h-4 mr-2" />
                      {updateProfile.isPending ? "Saving..." : "Save"}
                    </Button>
                  </div>
                )}
              </div>
            </CardHeader>
            <CardContent>
              {isEditing ? (
                <Form {...form}>
                  <form
                    id="profile-form"
                    onSubmit={form.handleSubmit(onSubmit)}
                    className="grid grid-cols-1 md:grid-cols-2 gap-4"
                  >
                    <FormField
                      control={form.control}
                      name="firstName"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>First Name</FormLabel>
                          <FormControl>
                            <Input
                              placeholder="Enter your first name"
                              {...field}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={form.control}
                      name="lastName"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Last Name</FormLabel>
                          <FormControl>
                            <Input
                              placeholder="Enter your last name"
                              {...field}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={form.control}
                      name="phoneNumber"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Phone Number</FormLabel>
                          <FormControl>
                            <div className="flex items-center">
                              <span className="flex items-center px-3 h-9 border border-r-0 rounded-l-md bg-muted text-sm text-muted-foreground">
                                +27
                              </span>
                              <Input
                                placeholder="81 234 5678"
                                className="rounded-l-none"
                                {...field}
                              />
                            </div>
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </form>
                </Form>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <p className="text-sm text-muted-foreground">First Name</p>
                    <p className="font-medium mt-1">
                      {profile.firstName?.trim() || "Not set"}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Last Name</p>
                    <p className="font-medium mt-1">
                      {profile.lastName?.trim() || "Not set"}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">
                      Phone Number
                    </p>
                    <p className="font-medium mt-1">
                      {stripCountryCode(profile.phoneNumber ?? "")
                        ? `(+27) ${stripCountryCode(profile.phoneNumber)}`
                        : "Not set"}
                    </p>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Organizations Card */}
          {profile.organizations && profile.organizations.length > 0 && (
            <Card className="bg-card border border-border shadow-sm">
              <CardHeader>
                <CardTitle className="text-xl flex items-center gap-2">
                  <Building2 className="w-5 h-5" />
                  Organisations
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {profile.organizations.map((org: OrganizationDto) => (
                    <div
                      key={org.id}
                      className="flex items-center justify-between p-4 border rounded-lg hover:bg-muted transition-colors"
                    >
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
                          <Building2 className="w-5 h-5 text-primary" />
                        </div>
                        <div>
                          <p className="font-semibold text-foreground">
                            {org.name}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        {currentOrganization?.id === org.id && (
                          <Badge variant="default">Current</Badge>
                        )}
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleViewOrgProfile(org.id)}
                          className="flex items-center gap-1"
                        >
                          <span>View</span>
                          <ArrowRight className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
        </div>

        {/* Right Column - Metadata */}
        <div className="space-y-6">
          <Card className="bg-card border border-border shadow-sm">
            <CardHeader>
              <CardTitle className="text-xl">Account Details</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-start gap-3">
                <Calendar className="w-5 h-5 text-muted-foreground mt-0.5" />
                <div>
                  <p className="text-sm text-muted-foreground">Joined</p>
                  <p className="font-medium">{formatDate(profile.createdAt)}</p>
                </div>
              </div>

              <Separator />

              <div className="flex items-start gap-3">
                <Clock className="w-5 h-5 text-muted-foreground mt-0.5" />
                <div>
                  <p className="text-sm text-muted-foreground">Last Login</p>
                  <p className="font-medium">
                    {formatDateTime(profile.lastLoginAt)}
                  </p>
                </div>
              </div>

              <Separator />

              <div className="flex items-start gap-3">
                <Building2 className="w-5 h-5 text-muted-foreground mt-0.5" />
                <div>
                  <p className="text-sm text-muted-foreground">
                    Current Organisation
                  </p>
                  <p className="font-medium">
                    {currentOrganization?.name || "None"}
                  </p>
                </div>
              </div>

              <Separator />

              <div className="flex items-start gap-3">
                <Shield className="w-5 h-5 text-muted-foreground mt-0.5" />
                <div>
                  <p className="text-sm text-muted-foreground">
                    Roles & Permissions
                  </p>
                  <div className="flex flex-wrap gap-1 mt-2">
                    {profile.roles.map((role: string) => (
                      <Badge key={role} variant="secondary">
                        {role}
                      </Badge>
                    ))}
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
          {/*Change Password*/}
          <Card className="bg-card border border-border shadow-sm">
            <CardHeader>
              <CardTitle className="text-xl">Change Password</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-start gap-3">
                <Button onClick={() => setChangePasswordOpen(true)}>
                  Change User Password
                </Button>
              </div>
            </CardContent>
          </Card>
          <ChangePasswordConfirmDialog
            open={changePasswordOpen}
            onOpenChange={setChangePasswordOpen}
          />
        </div>
      </div>
    </div>
  )
}
